import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CalendarPlus, Copy, MoreVertical, Plus, RotateCcw, Send, Sparkles, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/api/client";
import type { components } from "@dolfin/api-admin";
import { ConfirmActionDialog } from "@/components/ConfirmActionDialog";
import { DemoLinkResult, NewDemoDialog, PRESET_LABELS } from "@/components/NewDemoDialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Spinner } from "@/components/ui/spinner";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { formatDate, formatDateTime } from "@/lib/format";
import { formatProblem } from "@/lib/problemDetails";

type Demo = components["schemas"]["DemoAdminResponse"];
type DemoLink = components["schemas"]["DemoLinkAdminResponse"];
type DemoStatus = NonNullable<components["schemas"]["DemoStatus"]>;
type DemoStep = NonNullable<components["schemas"]["DemoStep"]>;

const STEP_LABELS: Record<DemoStep, string> = {
  Accounting: "connecting Xero",
  Documents: "adding documents",
  Bank: "connecting the bank",
  Stripe: "connecting Stripe",
  Books: "building the books",
};

const POLL_MS = 3000;

function problemText(error: unknown, fallback: string) {
  const p = formatProblem(error as Parameters<typeof formatProblem>[0], fallback);
  return p.detail ?? p.title;
}

/** Staff list of prospect demos (spec "AD - Demo Organisations"): create, re-link, seed, extend, reset, delete. */
export function DemosPage() {
  const [demos, setDemos] = useState<Demo[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    const { data, error: apiError } = await api.GET("/admin/demos", {});
    if (apiError || !data) {
      setError(problemText(apiError, "Couldn't load demos."));
      return;
    }
    setError(null);
    setDemos(data);
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  // Follow demos while they seed, so the step column moves without a reload.
  const seeding = demos?.some((d) => d.status === "Seeding") ?? false;
  useEffect(() => {
    if (!seeding) return;
    const id = setInterval(() => void load(), POLL_MS);
    return () => clearInterval(id);
  }, [seeding, load]);

  return (
    <div className="mx-auto w-full max-w-[100rem] p-8">
      <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="mb-1 text-2xl font-bold">Demos</h1>
          <p className="text-sm text-muted-foreground">
            Demo organisations for prospects, newest first. Each runs on made-up data and expires after 30 days unless
            extended.
          </p>
        </div>
        <Button onClick={() => setCreating(true)}>
          <Plus className="size-4" /> New demo
        </Button>
      </div>

      {error && !demos ? (
        <div className="rounded-md border border-destructive/30 bg-destructive/5 p-6 text-sm">
          <p className="text-destructive">{error}</p>
          <Button variant="outline" size="sm" className="mt-3" onClick={() => void load()}>
            Try again
          </Button>
        </div>
      ) : !demos ? (
        <div className="flex items-center gap-2 rounded-md border p-6 text-sm text-muted-foreground">
          <Spinner className="size-4" /> Loading demos
        </div>
      ) : demos.length === 0 ? (
        <div className="flex flex-col items-center rounded-md border border-dashed px-6 py-14 text-center">
          <Sparkles className="size-6 text-muted-foreground" />
          <p className="mt-3 text-sm font-semibold">No demos yet</p>
          <p className="mt-1 max-w-sm text-sm text-muted-foreground">
            Create one for a prospect: they get a magic link to a business with twelve months of books.
          </p>
          <Button size="sm" className="mt-4" onClick={() => setCreating(true)}>
            <Plus className="size-4" /> New demo
          </Button>
        </div>
      ) : (
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Prospect</TableHead>
                <TableHead>Company</TableHead>
                <TableHead>Business</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Created</TableHead>
                <TableHead>Expires</TableHead>
                <TableHead>Last sign-in</TableHead>
                <TableHead>Usage</TableHead>
                <TableHead className="w-10" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {demos.map((d) => (
                <DemoRow key={d.organisationId} demo={d} onChanged={load} />
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <NewDemoDialog open={creating} onOpenChange={setCreating} onCreated={() => void load()} />
    </div>
  );
}

function DemoRow({ demo, onChanged }: { demo: Demo; onChanged: () => Promise<void> }) {
  const id = demo.organisationId!;
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState<"reset" | "delete" | null>(null);
  const [link, setLink] = useState<DemoLink | null>(null);
  const expired = demo.expiresAt ? new Date(demo.expiresAt).getTime() < Date.now() : false;

  async function run<T>(call: () => Promise<{ data?: T; error?: unknown }>, done: string, failed: string) {
    setBusy(true);
    const { data, error } = await call();
    setBusy(false);
    if (error) {
      toast.error(problemText(error, failed));
      return null;
    }
    toast.success(done);
    await onChanged();
    return data ?? null;
  }

  async function mintLink(sendEmail: boolean) {
    const data = await run(
      () => api.POST("/admin/demos/{organisationId}/link", { params: { path: { organisationId: id } }, body: { sendEmail } }),
      sendEmail ? `Sent a new link to ${demo.prospectEmail}.` : "New link ready to copy.",
      "Couldn't make a new link.",
    );
    if (data && !sendEmail) setLink(data as DemoLink);
  }

  return (
    <TableRow>
      <TableCell>
        <div className="font-medium">{demo.prospectName || demo.prospectEmail}</div>
        {demo.prospectName ? <div className="text-xs text-muted-foreground">{demo.prospectEmail}</div> : null}
      </TableCell>
      <TableCell>
        <Link to={`/organisations/${id}`} className="font-medium underline-offset-4 hover:underline">
          {demo.companyName}
        </Link>
      </TableCell>
      <TableCell className="text-muted-foreground">{demo.preset ? PRESET_LABELS[demo.preset] : "-"}</TableCell>
      <TableCell>
        <StatusCell status={demo.status ?? "NotStarted"} step={demo.step ?? null} error={demo.error ?? null} />
      </TableCell>
      <TableCell className="whitespace-nowrap text-muted-foreground">{formatDate(demo.createdAt)}</TableCell>
      <TableCell className="whitespace-nowrap">
        {demo.expiresAt ? (
          <span className={expired ? "text-destructive" : undefined}>
            {formatDate(demo.expiresAt)}
            {expired ? " (expired)" : ""}
          </span>
        ) : (
          <span className="text-muted-foreground">Never</span>
        )}
      </TableCell>
      <TableCell className="whitespace-nowrap text-muted-foreground">
        {demo.lastSignInAt ? formatDateTime(demo.lastSignInAt) : "Not yet"}
      </TableCell>
      <TableCell className="whitespace-nowrap text-xs text-muted-foreground tabular-nums">
        <DemoUsage usage={demo.usage} />
      </TableCell>
      <TableCell className="text-right">
        {/* Non-modal: its items open dialogs, and a modal menu closing under a dialog leaves the page inert. */}
        <DropdownMenu modal={false}>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon-sm" disabled={busy} aria-label={`Actions for ${demo.companyName}`}>
              {busy ? <Spinner className="size-3.5" /> : <MoreVertical className="size-3.5" />}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem disabled={expired} onClick={() => void mintLink(false)}>
              <Copy className="size-3.5" /> Copy new link
            </DropdownMenuItem>
            <DropdownMenuItem disabled={expired} onClick={() => void mintLink(true)}>
              <Send className="size-3.5" /> Resend email
            </DropdownMenuItem>
            <DropdownMenuItem
              // No preset: the prospect picks the business on /try, and seeding without one is refused.
              disabled={demo.status === "Seeding" || demo.status === "Ready" || !demo.preset}
              onClick={() =>
                void run(
                  () => api.POST("/admin/demos/{organisationId}/seed", { params: { path: { organisationId: id } } }),
                  "Seeding started.",
                  "Couldn't start seeding.",
                )
              }
            >
              <Sparkles className="size-3.5" /> Seed now
            </DropdownMenuItem>
            <DropdownMenuItem
              disabled={!demo.expiresAt}
              onClick={() =>
                void run(
                  () =>
                    api.POST("/admin/demos/{organisationId}/extend", {
                      params: { path: { organisationId: id } },
                      body: { days: 30 },
                    }),
                  "Extended by 30 days.",
                  "Couldn't extend the demo.",
                )
              }
            >
              <CalendarPlus className="size-3.5" /> Extend 30 days
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => setConfirm("reset")}>
              <RotateCcw className="size-3.5" /> Reset
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => setConfirm("delete")} className="text-destructive">
              <Trash2 className="size-3.5" /> Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        <ConfirmActionDialog
          open={confirm === "reset"}
          onOpenChange={(o) => !o && setConfirm(null)}
          title={`Reset ${demo.companyName}?`}
          description="The prospect's changes are removed and the business is rebuilt, dated to today. Their sign-in keeps working."
          confirmLabel="Reset demo"
          pending={busy}
          onConfirm={async () => {
            await run(
              () => api.POST("/admin/demos/{organisationId}/reset", { params: { path: { organisationId: id } } }),
              "Reset started.",
              "Couldn't reset the demo.",
            );
            setConfirm(null);
          }}
        />
        <ConfirmActionDialog
          open={confirm === "delete"}
          onOpenChange={(o) => !o && setConfirm(null)}
          title={`Delete ${demo.companyName}?`}
          description="The demo organisation, its data and the prospect's sign-in are removed for good."
          confirmLabel="Delete demo"
          pending={busy}
          onConfirm={async () => {
            await run(
              () => api.DELETE("/admin/demos/{organisationId}", { params: { path: { organisationId: id } } }),
              "Demo deleted.",
              "Couldn't delete the demo.",
            );
            setConfirm(null);
          }}
        />
        <Dialog open={link !== null} onOpenChange={(o) => !o && setLink(null)}>
          <DialogContent>{link ? <DemoLinkResult link={link} title="New magic link" /> : null}</DialogContent>
        </Dialog>
      </TableCell>
    </TableRow>
  );
}

function StatusCell({ status, step, error }: { status: DemoStatus; step: DemoStep | null; error: string | null }) {
  const badge = (() => {
    switch (status) {
      case "Ready":
        return <Badge>Ready</Badge>;
      case "Seeding":
        return (
          <Badge variant="secondary" className="gap-1">
            <Spinner className="size-3" /> Seeding
          </Badge>
        );
      case "Failed":
        return <Badge variant="destructive">Failed</Badge>;
      default:
        return <Badge variant="outline">Not started</Badge>;
    }
  })();
  return (
    <div className="flex items-center gap-2">
      {status === "Failed" && error ? (
        <Tooltip>
          <TooltipTrigger asChild>
            <span>{badge}</span>
          </TooltipTrigger>
          <TooltipContent className="max-w-xs">{error}</TooltipContent>
        </Tooltip>
      ) : (
        badge
      )}
      {status === "Seeding" && step ? <span className="text-xs text-muted-foreground">{STEP_LABELS[step]}</span> : null}
    </div>
  );
}

/** What the prospect has made against the demo's caps; a kind at its cap is highlighted. */
function DemoUsage({ usage }: { usage: Demo["usage"] }) {
  const limit = Number(usage.limit ?? 0);
  const kinds = [
    ["bills", Number(usage.bills ?? 0)],
    ["invoices", Number(usage.invoices ?? 0)],
    ["receipts", Number(usage.receipts ?? 0)],
  ] as const;
  return (
    <>
      {kinds.map(([label, used], i) => (
        <span key={label} className={used >= limit ? "font-medium text-destructive" : undefined}>
          {i > 0 ? " · " : null}
          {label} {used}/{limit}
        </span>
      ))}
    </>
  );
}
